import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Quote } from '@client/model';
import { AppState } from '@client/model/store';
import { useLtcPlanningQuotes } from './useLtcPlanningQuotes';

export type PaymentOption = 'Single Pay' | '10-Pay' | 'Monthly' | 'Annually';

export interface PaymentOptionGroup {
  paymentOption: PaymentOption;
  quotes: Quote[];
  isPredicted: boolean;
}

const PAYMENT_OPTIONS: PaymentOption[] = ['Single Pay', '10-Pay', 'Monthly', 'Annually'];

function getPaymentOptionFromQuote(quote: Quote): PaymentOption {
  if (quote.cobPaymentOption) {
    const normalized = quote.cobPaymentOption.toLowerCase();
    if (normalized.includes('single')) return 'Single Pay';
    if (normalized.includes('10') || normalized.includes('ten')) return '10-Pay';
    if (normalized.includes('monthly')) return 'Monthly';
    if (normalized.includes('annual')) return 'Annually';
  }

  if (quote.productFundingOption) {
    const normalized = quote.productFundingOption.toLowerCase();
    if (normalized.includes('single')) return 'Single Pay';
    if (normalized.includes('10') || normalized.includes('ten')) return '10-Pay';
    if (normalized.includes('monthly')) return 'Monthly';
    if (normalized.includes('annual')) return 'Annually';
  }

  return 'Single Pay';
}

function getPredictedOptions(
  paymentStructure: LtcFinancialPlanningPaymentStructure | null,
): Set<PaymentOption> {
  const predicted = new Set<PaymentOption>();

  if (!paymentStructure || paymentStructure === 'noPreference') {
    return predicted;
  }

  switch (paymentStructure) {
    case 'upfront':
      predicted.add('Single Pay');
      break;
    case 'monthly':
      predicted.add('Monthly');
      break;
    case 'balanced':
      predicted.add('10-Pay');
      break;
  }

  return predicted;
}

interface UseLtcPlanningQuotesByPaymentOptionOptions {
  enabled?: boolean;
  topN?: number;
}

export function useLtcPlanningQuotesByPaymentOption(
  options: UseLtcPlanningQuotesByPaymentOptionOptions = {},
) {
  const { enabled = true, topN = 3 } = options;

  const { preferences } = useSelector(
    (state: { ltcFinancialPlanning: AppState['ltcFinancialPlanning'] }) =>
      state.ltcFinancialPlanning,
  );

  const { allQuotes, isLoading, selectedPolicyTypes, getPolicyTypeLabel } = useLtcPlanningQuotes({
    enabled,
  });

  const predictedOptions = useMemo(() => {
    return getPredictedOptions(preferences.paymentStructure);
  }, [preferences.paymentStructure]);

  const groupedQuotes = useMemo(() => {
    const grouped: Record<PaymentOption, Quote[]> = {
      'Single Pay': [],
      '10-Pay': [],
      Monthly: [],
      Annually: [],
    };

    allQuotes.forEach(quote => {
      const paymentOption = getPaymentOptionFromQuote(quote);
      if (grouped[paymentOption]) {
        grouped[paymentOption].push(quote);
      }
    });

    return grouped;
  }, [allQuotes]);

  const paymentOptionGroups = useMemo((): PaymentOptionGroup[] => {
    const groups: PaymentOptionGroup[] = PAYMENT_OPTIONS.map(paymentOption => ({
      paymentOption,
      quotes: groupedQuotes[paymentOption].slice(0, topN),
      isPredicted: predictedOptions.has(paymentOption),
    }));

    groups.sort((a, b) => {
      if (a.isPredicted && !b.isPredicted) return -1;
      if (!a.isPredicted && b.isPredicted) return 1;
      return 0;
    });

    return groups;
  }, [groupedQuotes, predictedOptions, topN]);

  const nonEmptyGroups = useMemo(() => {
    return paymentOptionGroups.filter(group => group.quotes.length > 0);
  }, [paymentOptionGroups]);

  const hasAnyQuotes = useMemo(() => {
    return nonEmptyGroups.some(group => group.quotes.length > 0);
  }, [nonEmptyGroups]);

  const totalCount = useMemo(() => {
    return allQuotes.length;
  }, [allQuotes]);

  return {
    paymentOptionGroups: nonEmptyGroups,
    allGroups: paymentOptionGroups,
    isLoading,
    hasAnyQuotes,
    totalCount,
    selectedPolicyTypes,
    getPolicyTypeLabel,
  };
}

